/**
 * Очередь заказов F&B «Кухня и бар» — /menu/orders и /fnb-queue.
 *
 * Дизайн manager v3 §9.5 «Заказы (real-time)»: kanban из 4 колонок
 *   NEW → COOKING → READY → DELIVERED
 * (статусы РОВНО как в дизайне). Тикет: ORD-{seq}, номер комнаты, время,
 * список позиций (компактно, из description), цех (кухня/бар = station),
 * аватар исполнителя. В шапке — «● Live» chip + «Сегодня · N». SLA-warn,
 * если заявка висит в NEW дольше 5 минут. Клик по тикету → деталь
 * (модификаторы / пожелания / аллергии из описания). На 375 — вкладки
 * вместо kanban. Empty: «Сейчас заказов нет. Гости отдыхают».
 *
 * Жизненный цикл заказа = заявка request: Принять (assign) → Готовится
 * (start) → Готово (resolve) → Доставлено (deliver, resolved→delivered).
 * Источник — GET /v1/admin/requests (polling), делим по цеху (deptOfRequest).
 *
 * RBAC: менеджер/владелец/официант видят оба цеха (фильтр-чипы); узкий
 * сотрудник (кухня ИЛИ бар) — только свой цех. Гость сюда не попадает (nav).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@hsc/i18n';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { ManagerRequest } from '@hsc/types';
import { usePlatformMe } from '../../shared/api/usePlatformMe.js';
import { resolveDashboardKind } from '../../shared/nav/resolve-role.js';
import { Toast } from '../../shared/ui/Toast.js';
import { deptOfRequest, useFnbQueue, useFnbTransitions, type FnbDept } from './useFnbQueue.js';
import { useMyStaffId } from './useMyStaffId.js';

/** Колонки-стадии очереди §9.5. */
type Stage = 'new' | 'cooking' | 'ready' | 'delivered';
const STAGES: readonly Stage[] = ['new', 'cooking', 'ready', 'delivered'];

/** Порог SLA: заявка в NEW дольше — предупреждаем (§9.5 «не взяли >5 мин»). */
const SLA_WARN_MS = 5 * 60 * 1000;

function stageOf(state: string): Stage | null {
  switch (state) {
    case 'created':
    case 'assigned':
    case 'escalated':
      return 'new';
    case 'in_progress':
      return 'cooking';
    case 'resolved':
      return 'ready';
    case 'delivered':
      return 'delivered';
    default:
      return null;
  }
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** «ORD-{seq}» — пер-тенантный номер заявки; до бэкфилла seq=0 → короткий id. */
function orderRef(req: ManagerRequest): string {
  if (req.seq !== undefined && req.seq > 0) return `ORD-${req.seq}`;
  return `ORD-${req.id.slice(0, 4).toUpperCase()}`;
}

/** Инициалы исполнителя для аватара (или «—», если не назначен). */
function initials(name: string | undefined): string {
  if (name === undefined || name.trim() === '') return '—';
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Сколько минут заявка висит без работы (для SLA-варна в NEW). */
function ageMs(iso: string): number {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : Date.now() - t;
}

/** Подпись одной позиции заказа: «Название · вариант × qty» (#74). */
function itemLabel(it: NonNullable<ManagerRequest['items']>[number]): string {
  const base = it.variant !== undefined && it.variant !== '' ? `${it.name} · ${it.variant}` : it.name;
  return `${base} × ${it.qty}`;
}

/** Карточка одного заказа в очереди + действия стадии. */
function OrderCard({
  req,
  t,
  onAccept,
  onStart,
  onFinish,
  onDeliver,
  onOpen,
  busy,
}: {
  req: ManagerRequest;
  t: (k: string) => string;
  onAccept: (req: ManagerRequest) => void;
  onStart: (req: ManagerRequest) => void;
  onFinish: (req: ManagerRequest) => void;
  onDeliver: (req: ManagerRequest) => void;
  onOpen: (req: ManagerRequest) => void;
  busy: boolean;
}): JSX.Element {
  const stage = stageOf(req.state);
  const dept = deptOfRequest(req);
  const deptLabel = dept === 'bar' ? t('fnbQueue.dept.bar') : t('fnbQueue.dept.kitchen');
  const slaWarn = stage === 'new' && ageMs(req.created_at) > SLA_WARN_MS;
  const waitMin = Math.floor(ageMs(req.created_at) / 60000);

  return (
    <li
      className={`fnbq-card fnbq-card--${dept ?? 'kitchen'}${stage === 'delivered' ? ' fnbq-card--done' : ''}`}
      data-testid={`fnbq-order-${req.id}`}
    >
      <button
        type="button"
        className="fnbq-card__open"
        data-testid={`fnbq-open-${req.id}`}
        onClick={() => onOpen(req)}
      >
        <div className="fnbq-card__head">
          <span className="fnbq-card__ord">{orderRef(req)}</span>
          {req.room_number !== undefined && req.room_number !== '' && (
            <span className="fnbq-card__room">№{req.room_number}</span>
          )}
          <span className="fnbq-card__time">{fmtWhen(req.created_at)}</span>
        </div>
        <div className="fnbq-card__title">{req.title}</div>
        {req.items !== undefined && req.items.length > 0 ? (
          // Заказ из меню (#74): структурный список позиций на карточке.
          <ul className="fnbq-card__items" data-testid={`fnbq-items-${req.id}`}>
            {req.items.map((it, i) => (
              <li key={`${it.name}-${i}`}>{itemLabel(it)}</li>
            ))}
          </ul>
        ) : (
          req.description !== undefined &&
          req.description !== '' && <p className="fnbq-card__desc">{req.description}</p>
        )}
        <div className="fnbq-card__meta">
          <span className={`fnbq-badge fnbq-badge--${dept ?? 'kitchen'}`}>{deptLabel}</span>
          {slaWarn ? (
            <span className="fnbq-sla fnbq-sla--warn" data-testid={`fnbq-sla-${req.id}`}>
              ⚠ {waitMin} мин · {t('fnbQueue.sla')}
            </span>
          ) : (
            <span className="fnbq-avatar" aria-label={req.assignee_name ?? ''}>
              {initials(req.assignee_name)}
            </span>
          )}
        </div>
      </button>

      <div className="fnbq-card__actions">
        {req.state === 'created' && (
          <Button data-testid={`fnbq-accept-${req.id}`} loading={busy} onClick={() => onAccept(req)}>
            {t('fnbQueue.action.accept')}
          </Button>
        )}
        {req.state === 'assigned' && (
          <Button data-testid={`fnbq-start-${req.id}`} loading={busy} onClick={() => onStart(req)}>
            {t('fnbQueue.action.start')}
          </Button>
        )}
        {req.state === 'in_progress' && (
          <Button data-testid={`fnbq-finish-${req.id}`} loading={busy} onClick={() => onFinish(req)}>
            {t('fnbQueue.action.finish')}
          </Button>
        )}
        {req.state === 'resolved' && (
          <Button data-testid={`fnbq-deliver-${req.id}`} loading={busy} onClick={() => onDeliver(req)}>
            {t('fnbQueue.action.deliver')}
          </Button>
        )}
        {req.state === 'delivered' && (
          <span className="fnbq-done-mark" data-testid={`fnbq-done-${req.id}`}>
            ✓ {t('fnbQueue.stage.delivered')}
          </span>
        )}
      </div>
    </li>
  );
}

/** Деталь заказа — оверлей (модификаторы / пожелания / аллергии из описания). */
function OrderDetail({
  req,
  t,
  onClose,
}: {
  req: ManagerRequest;
  t: (k: string) => string;
  onClose: () => void;
}): JSX.Element {
  const dept = deptOfRequest(req);
  const deptLabel = dept === 'bar' ? t('fnbQueue.dept.bar') : t('fnbQueue.dept.kitchen');
  return (
    <div className="fnbq-detail-backdrop">
      <button
        type="button"
        className="fnbq-detail-backdrop__close"
        aria-label={t('fnbQueue.detail.close')}
        onClick={onClose}
      />
      <div
        className="fnbq-detail"
        role="dialog"
        aria-modal="true"
        aria-label={`${t('fnbQueue.detail.title')} ${orderRef(req)}`}
        data-testid="fnbq-detail"
      >
        <div className="fnbq-detail__head">
          <h2 className="fnbq-detail__title">
            {orderRef(req)}
            {req.room_number !== undefined && req.room_number !== '' && ` · №${req.room_number}`}
          </h2>
          <button
            type="button"
            className="fnbq-detail__close"
            aria-label={t('fnbQueue.detail.close')}
            data-testid="fnbq-detail-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <dl className="fnbq-detail__list">
          <dt>{t('fnbQueue.detail.items')}</dt>
          <dd>
            {req.items !== undefined && req.items.length > 0 ? (
              <ul className="fnbq-detail__items" data-testid="fnbq-detail-items">
                {req.items.map((it, i) => (
                  <li key={`${it.name}-${i}`}>{itemLabel(it)}</li>
                ))}
              </ul>
            ) : (
              req.title
            )}
          </dd>
          <dt>{t('fnbQueue.detail.station')}</dt>
          <dd>{deptLabel}</dd>
          {req.description !== undefined && req.description !== '' && (
            <>
              <dt>{t('fnbQueue.detail.wishes')}</dt>
              <dd>{req.description}</dd>
            </>
          )}
          {req.assignee_name !== undefined && req.assignee_name !== '' && (
            <>
              <dt>{t('fnbQueue.dept.filter')}</dt>
              <dd>{req.assignee_name}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

export function FnbQueuePage(): JSX.Element {
  const { t } = useTranslation('requests');
  const me = usePlatformMe();
  const kind = me.data ? resolveDashboardKind(me.data.actor.roles) : 'staff';
  const roles = useMemo(() => me.data?.actor.roles ?? [], [me.data]);

  const { fnb, isLoading, isError, refetch } = useFnbQueue();
  const { accept, start, finish, deliver } = useFnbTransitions();
  const myStaff = useMyStaffId();
  const [dept, setDept] = useState<FnbDept | 'all'>('all');
  // На узком экране (375) kanban не помещается — показываем по одной стадии,
  // переключаемой вкладками (§9.5: «375 → tabs вместо kanban»).
  const [tab, setTab] = useState<Stage>('new');
  const [toast, setToast] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openReq, setOpenReq] = useState<ManagerRequest | null>(null);

  // «Сегодня · N»: заявки очереди, созданные сегодня (для chip в шапке).
  const todayCount = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return fnb.filter((r) => Date.parse(r.created_at) >= start.getTime()).length;
  }, [fnb]);

  const isManagerRole = kind === 'owner' || kind === 'manager' || roles.includes('front_desk');
  const lockedDept: FnbDept | null =
    !isManagerRole && myStaff.fnbDepts.length === 1 ? myStaff.fnbDepts[0]! : null;
  const canSeeBoth = isManagerRole || (!lockedDept && myStaff.fnbDepts.length !== 1);
  const effectiveDept: FnbDept | 'all' = lockedDept ?? dept;

  const visible = useMemo(() => {
    const ordered = [...fnb].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    if (effectiveDept === 'all') return ordered;
    return ordered.filter((r) => deptOfRequest(r) === effectiveDept);
  }, [fnb, effectiveDept]);

  const groups: Record<Stage, ManagerRequest[]> = useMemo(() => {
    const g: Record<Stage, ManagerRequest[]> = { new: [], cooking: [], ready: [], delivered: [] };
    for (const r of visible) {
      const s = stageOf(r.state);
      if (s) g[s].push(r);
    }
    return g;
  }, [visible]);

  // Тикает каждую минуту, чтобы SLA-варн и время «жизни» в NEW обновлялись
  // без действия пользователя (polling данных — отдельно, в useFnbQueue).
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNow((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const busyId =
    accept.isPending || start.isPending || finish.isPending || deliver.isPending
      ? (accept.variables?.id ??
        (start.variables as string | undefined) ??
        (finish.variables as string | undefined) ??
        (deliver.variables as string | undefined))
      : undefined;

  function handleAccept(req: ManagerRequest): void {
    setErr(null);
    if (myStaff.staffId === undefined) {
      setErr(t('fnbQueue.error.noStaffId'));
      return;
    }
    accept.mutate(
      { id: req.id, staffId: myStaff.staffId },
      {
        onError: () => setErr(t('fnbQueue.error.transition')),
        onSuccess: () => setToast(t('fnbQueue.toast.accepted')),
      },
    );
  }
  function handleStart(req: ManagerRequest): void {
    setErr(null);
    start.mutate(req.id, {
      onError: () => setErr(t('fnbQueue.error.transition')),
      onSuccess: () => setToast(t('fnbQueue.toast.cooking')),
    });
  }
  function handleFinish(req: ManagerRequest): void {
    setErr(null);
    finish.mutate(req.id, {
      onError: () => setErr(t('fnbQueue.error.transition')),
      onSuccess: () => setToast(t('fnbQueue.toast.ready')),
    });
  }
  function handleDeliver(req: ManagerRequest): void {
    setErr(null);
    deliver.mutate(req.id, {
      onError: () => setErr(t('fnbQueue.error.transition')),
      onSuccess: () => setToast(t('fnbQueue.toast.delivered')),
    });
  }

  function colTitle(stage: Stage): string {
    switch (stage) {
      case 'new':
        return t('fnbQueue.col.new');
      case 'cooking':
        return t('fnbQueue.col.cooking');
      case 'ready':
        return t('fnbQueue.col.ready');
      case 'delivered':
        return t('fnbQueue.col.delivered');
    }
  }

  const renderCol = (stage: Stage): JSX.Element => (
    <div className="fnbq-col" key={stage} data-testid={`fnbq-col-${stage}`}>
      <h2 className="fnbq-col__title">
        {colTitle(stage)}
        <span className="fnbq-col__count">{groups[stage].length}</span>
      </h2>
      {groups[stage].length === 0 ? (
        <p className="fnbq-col__empty">{t('fnbQueue.col.empty')}</p>
      ) : (
        <ul className="fnbq-list">
          {groups[stage].map((req) => (
            <OrderCard
              key={req.id}
              req={req}
              t={t}
              busy={busyId === req.id}
              onAccept={handleAccept}
              onStart={handleStart}
              onFinish={handleFinish}
              onDeliver={handleDeliver}
              onOpen={setOpenReq}
            />
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <section data-testid="fnb-queue-page">
      <div className="fnbq-topbar">
        <div>
          <h1 className="hsc-page__title">{t('fnbQueue.title')}</h1>
          <p className="ta-muted">{t('fnbQueue.subtitle')}</p>
        </div>
        <div className="fnbq-topbar__chips">
          <span className="fnbq-live" data-testid="fnbq-live">
            ● {t('fnbQueue.live')}
          </span>
          <span className="fnbq-today" data-testid="fnbq-today">
            {t('fnbQueue.today')} · {todayCount}
          </span>
        </div>
      </div>

      {lockedDept !== null && (
        <p className="fnbq-station" data-testid="fnbq-station">
          {lockedDept === 'bar' ? t('fnbQueue.station.bar') : t('fnbQueue.station.kitchen')}
        </p>
      )}

      {canSeeBoth && (
        <div className="fnbq-chips" role="group" aria-label={t('fnbQueue.dept.filter')}>
          {(['all', 'kitchen', 'bar'] as const).map((d) => (
            <button
              key={d}
              type="button"
              className={`fnbq-chip${dept === d ? ' is-active' : ''}`}
              data-testid={`fnbq-chip-${d}`}
              aria-pressed={dept === d}
              onClick={() => setDept(d)}
            >
              {d === 'all'
                ? t('fnbQueue.dept.all')
                : d === 'kitchen'
                  ? t('fnbQueue.dept.kitchen')
                  : t('fnbQueue.dept.bar')}
            </button>
          ))}
        </div>
      )}

      {toast !== null && <Toast message={toast} />}
      {err !== null && (
        <div className="ta-error-summary" role="alert" data-testid="fnbq-error">
          {err}
        </div>
      )}

      {isLoading ? (
        <div className="ta-grid" data-testid="fnbq-loading" aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={96} radius={14} />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <p role="alert">{t('fnbQueue.error.load')}</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              {t('fnbQueue.action.retry')}
            </Button>
          </div>
        </Card>
      ) : visible.length === 0 ? (
        <Card data-testid="fnbq-empty">
          <p>{t('fnbQueue.empty')}</p>
        </Card>
      ) : (
        <>
          {/* 375 → вкладки стадий (kanban не помещается). */}
          <div className="fnbq-tabs" role="tablist" data-testid="fnbq-tabs">
            {STAGES.map((stage) => (
              <button
                key={stage}
                type="button"
                role="tab"
                aria-selected={tab === stage}
                className={`fnbq-tab${tab === stage ? ' is-active' : ''}`}
                data-testid={`fnbq-tab-${stage}`}
                onClick={() => setTab(stage)}
              >
                {colTitle(stage)} <span className="fnbq-tab__count">{groups[stage].length}</span>
              </button>
            ))}
          </div>

          {/* 1440 → kanban из 4 колонок (§9.5). */}
          <div className="fnbq-board" data-testid="fnbq-board">
            {STAGES.map(renderCol)}
          </div>

          {/* На 375 показываем только активную вкладку. */}
          <div className="fnbq-tabpanel" data-testid="fnbq-tabpanel">
            {renderCol(tab)}
          </div>
        </>
      )}

      {openReq !== null && <OrderDetail req={openReq} t={t} onClose={() => setOpenReq(null)} />}
    </section>
  );
}
